import { useEffect, useRef } from "react";
import {
  RECORDER_IDLE,
  reduceRecorder,
  tokenFromKey,
  type RecorderState,
} from "./hotkey-recorder-logic";

export interface RecorderHandlers {
  /** All keys were released: `tokens` is the peak set of keys held together. */
  onCommit: (tokens: readonly string[]) => void;
  /** Escape alone was pressed. */
  onCancel: () => void;
  /** Only modifier keys were held. */
  onIgnore: () => void;
  /** The keys held so far (peak set), while recording, for the live combination display. */
  onPending?: (tokens: readonly string[]) => void;
}

/**
 * Listens for key events while `active`. Capture phase, and default prevented, so browser
 * shortcuts do not fire while a hotkey is being recorded. Losing focus cancels.
 */
export function useHotkeyRecorder(active: boolean, handlers: RecorderHandlers): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!active) return;
    let state: RecorderState = RECORDER_IDLE;

    const dispatch = (type: "down" | "up", event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const token = tokenFromKey(event);
      if (token === null) return;
      const outcome = reduceRecorder(state, { type, token });
      if (outcome.kind === "pending") {
        state = outcome.state;
        handlersRef.current.onPending?.(outcome.state.peak);
        return;
      }
      state = RECORDER_IDLE;
      if (outcome.kind === "commit") handlersRef.current.onCommit(outcome.tokens);
      else if (outcome.kind === "cancel") handlersRef.current.onCancel();
      else handlersRef.current.onIgnore();
    };

    const onDown = (event: KeyboardEvent) => dispatch("down", event);
    const onUp = (event: KeyboardEvent) => dispatch("up", event);
    const onBlur = () => {
      state = RECORDER_IDLE;
      handlersRef.current.onCancel();
    };

    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [active]);
}
